import { ChevronDown, ChevronRight, ChevronUp, Plus, Trash2, Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Boton, BotonDeIcono, Pestanas as PestanasUI, clases } from '../../componentes/ui'
import { CampoGenerico } from '../Campo'
import { BloqueDePlantilla, esBloqueDePlantilla, usePlantilla } from '../../estructura/Vinculos'
import { hayErroresEn, hayErroresEnAlguno, primeraConErrores, rutasDeLosCampos } from '../errores'
import { type Campo, valorInicial } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { idDeFila, unir } from '../rutas'

/**
 * Los campos que contienen otros campos: grupos, filas repetibles (arrays), bloques,
 * pestañas, filas de maquetación y plegables. Cada uno delega en `CampoGenerico`
 * para lo que lleva dentro, con la ruta ya compuesta.
 *
 * Lo que tiene un error dentro no se queda escondido: el plegable se abre, la fila
 * plegada se despliega y la pestaña con el error se elige (auditoría del 29-09-2026, M6).
 * Y quitar un bloque o una fila se puede deshacer (M1).
 */
type Fila = Record<string, unknown> & { id?: string; _clave?: string }

const claveDe = (fila: Fila, i: number) => String(fila.id ?? fila._clave ?? i)

/**
 * El título de una fila plegada: su primer texto escrito. Un vínculo de una plantilla
 * («@titulo») no es un título: se dice que va vinculado.
 */
const tituloDeFila = (fila: Fila, campos: Campo[]): string => {
  for (const c of campos) {
    const valor = c.nombre ? fila[c.nombre] : undefined
    if (!c.nombre || (c.tipo !== 'text' && c.tipo !== 'textarea') || typeof valor !== 'string' || !valor) continue
    return valor.startsWith('@') ? `${c.etiqueta}: vinculado` : valor.slice(0, 60)
  }
  return ''
}

export const Grupo = ({ campo, ruta }: { campo: Campo; ruta: string }) => {
  const hijos = (campo.campos ?? []).map((hijo, i) => <CampoGenerico campo={hijo} key={hijo.nombre ?? i} ruta={unir(ruta, hijo.nombre)} />)
  // Sin rótulo propio (su pestaña ya lo dice): sin recuadro, como si sus campos fueran de la pestaña.
  if (campo.sinEtiqueta || !campo.etiqueta) {
    return (
      <div className="flex flex-col gap-4">
        {campo.descripcion && <p className="text-xs text-tenue">{campo.descripcion}</p>}
        {hijos}
      </div>
    )
  }
  return (
    <fieldset className="rounded-caja border border-linea bg-papel-2/50 p-4">
      <legend className="px-1.5 text-[13px] font-semibold">{campo.etiqueta}</legend>
      {campo.descripcion && <p className="mb-3 text-xs text-tenue">{campo.descripcion}</p>}
      <div className="flex flex-col gap-4">{hijos}</div>
    </fieldset>
  )
}

export const FilaDeMaquetacion = ({ campo, ruta }: { campo: Campo; ruta: string }) => (
  <div className="grid gap-4 md:grid-cols-2">
    {(campo.campos ?? []).map((hijo, i) => (
      <CampoGenerico campo={hijo} key={hijo.nombre ?? i} ruta={unir(ruta, hijo.nombre)} />
    ))}
  </div>
)

export const Plegable = ({ campo, ruta }: { campo: Campo; ruta: string }) => {
  // Abierto salvo que el núcleo diga que arranca cerrado (`initCollapsed`), como en Payload.
  const [abierto, setAbierto] = useState(!campo.plegado)
  const { errores } = useFormulario()
  const conError = hayErroresEnAlguno(errores, rutasDeLosCampos(campo.campos ?? [], ruta))
  useEffect(() => {
    if (conError) setAbierto(true)
  }, [conError])
  return (
    <div className={clases('rounded-caja border', conError ? 'border-error/40' : 'border-linea')}>
      <button aria-expanded={abierto} className="flex w-full items-center gap-2 px-4 py-3 text-left text-[13px] font-semibold hover:bg-papel-2" onClick={() => setAbierto((v) => !v)} type="button">
        {abierto ? <ChevronDown className="size-4 text-tenue" /> : <ChevronRight className="size-4 text-tenue" />}
        {campo.etiqueta}
        {conError && <span aria-label="con errores" className="size-1.5 rounded-full bg-error" />}
      </button>
      {abierto && (
        <div className="flex flex-col gap-4 border-t border-linea p-4">
          {campo.descripcion && <p className="text-xs text-tenue">{campo.descripcion}</p>}
          {(campo.campos ?? []).map((hijo, i) => (
            <CampoGenerico campo={hijo} key={hijo.nombre ?? i} ruta={unir(ruta, hijo.nombre)} />
          ))}
        </div>
      )}
    </div>
  )
}

export const Pestanas = ({ campo, ruta }: { campo: Campo; ruta: string }) => {
  const pestanas = campo.pestanas ?? []
  const [activa, setActiva] = useState(0)
  const { errores } = useFormulario()
  const conError = (i: number) => {
    const p = pestanas[i]
    if (!p) return false
    return hayErroresEnAlguno(errores, p.nombre ? [unir(ruta, p.nombre)] : rutasDeLosCampos(p.campos, ruta))
  }
  const marcas = pestanas.map((_, i) => conError(i))
  const clave = marcas.join(',')
  // Si guardar devuelve errores y la pestaña abierta no tiene ninguno, se va a la primera que sí.
  useEffect(() => {
    if (marcas[activa]) return
    const primera = primeraConErrores(
      pestanas.map((_, i) => String(i)),
      new Set(marcas.flatMap((m, i) => (m ? [String(i)] : []))),
    )
    if (primera !== null) setActiva(Number(primera))
    // Solo cuando cambia qué pestañas tienen errores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave])
  const descripcion = pestanas[activa]?.descripcion
  return (
    <div>
      <div className="mb-5">
        <PestanasUI activa={String(activa)} alCambiar={(c) => setActiva(Number(c))} pestanas={pestanas.map((p, i) => ({ clave: String(i), etiqueta: p.etiqueta, aviso: marcas[i] }))} />
      </div>
      {descripcion && <p className="-mt-2 mb-4 text-sm text-tenue">{descripcion}</p>}
      {pestanas.map((p, i) => (
        <div className={clases('flex flex-col gap-5', activa !== i && 'hidden')} key={p.etiqueta} role="tabpanel">
          {p.campos.map((hijo, j) => (
            <CampoGenerico campo={hijo} key={hijo.nombre ?? j} ruta={unir(unir(ruta, p.nombre), hijo.nombre)} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Una fila de un array o un bloque: plegable, con su título, y movible. Quitar va aparte de mover, para no darle sin querer. */
const Tarjeta = ({
  titulo,
  subtitulo,
  indice,
  total,
  mover,
  quitar,
  etiquetaDeQuitar,
  conError,
  soloLectura,
  children,
  clave,
  ultimaAnadida,
}: {
  children: React.ReactNode
  /** La clave de esta fila y la de la última que se ha añadido: al añadir una, las demás se pliegan. */
  clave?: string
  ultimaAnadida?: null | string
  conError: boolean
  etiquetaDeQuitar: string
  indice: number
  mover: (desde: number, hasta: number) => void
  quitar: () => void
  soloLectura: boolean
  subtitulo?: string
  titulo: string
  total: number
}) => {
  const [abierta, setAbierta] = useState(true)
  useEffect(() => {
    if (conError) setAbierta(true)
  }, [conError])
  // Una fila nueva se abre sola; las de antes se pliegan para que la nueva se vea sin bajar una pantalla entera.
  useEffect(() => {
    if (ultimaAnadida && ultimaAnadida !== clave && !conError) setAbierta(false)
    // Solo al añadir una fila, no cuando cambian sus errores.
  }, [ultimaAnadida])
  return (
    <div className={clases('rounded-caja border bg-papel shadow-suave', conError ? 'border-error/40' : 'border-linea')}>
      <div className={clases('flex items-center gap-2 px-3 py-2', abierta && 'border-b border-linea')}>
        <button aria-expanded={abierta} className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setAbierta((v) => !v)} type="button">
          {abierta ? <ChevronDown className="size-4 shrink-0 text-tenue" /> : <ChevronRight className="size-4 shrink-0 text-tenue" />}
          <span className="truncate text-[13px] font-medium">{titulo || <span className="font-normal text-tenue">Sin título</span>}</span>
          {subtitulo && <span className="rounded-pastilla border border-violeta-borde bg-violeta-suave px-1.5 py-px text-[11px] font-medium text-violeta">{subtitulo}</span>}
          {conError && <span aria-label="con errores" className="size-1.5 shrink-0 rounded-full bg-error" />}
        </button>
        {!soloLectura && (
          <div className="flex shrink-0 items-center gap-0.5">
            <BotonDeIcono disabled={indice === 0} etiqueta="Subir" onClick={() => mover(indice, indice - 1)} pequeno>
              <ChevronUp />
            </BotonDeIcono>
            <BotonDeIcono disabled={indice === total - 1} etiqueta="Bajar" onClick={() => mover(indice, indice + 1)} pequeno>
              <ChevronDown />
            </BotonDeIcono>
            <span aria-hidden className="mx-2 h-5 w-px bg-linea-fuerte" />
            <BotonDeIcono etiqueta={etiquetaDeQuitar} onClick={quitar} pequeno tipo="peligro">
              <Trash2 />
            </BotonDeIcono>
          </div>
        )}
      </div>
      {abierta && <div className="flex flex-col gap-4 p-4">{children}</div>}
    </div>
  )
}

type Quitado = { fila: Fila; indice: number; nombre: string }

/** Cuánto se ofrece «Deshacer» después de quitar una fila. */
const SEGUNDOS_PARA_DESHACER = 20

const useFilas = (ruta: string) => {
  const { valor, cambiar, error } = useValor<Fila[]>(ruta)
  const filas = Array.isArray(valor) ? valor : []
  const [quitado, setQuitado] = useState<null | Quitado>(null)
  useEffect(() => {
    if (!quitado) return
    const t = window.setTimeout(() => setQuitado(null), SEGUNDOS_PARA_DESHACER * 1000)
    return () => window.clearTimeout(t)
  }, [quitado])
  return {
    filas,
    error,
    quitado,
    poner: (siguientes: Fila[]) => cambiar(siguientes),
    mover: (desde: number, hasta: number) => {
      if (hasta < 0 || hasta >= filas.length) return
      const copia = [...filas]
      const [fila] = copia.splice(desde, 1)
      if (fila) copia.splice(hasta, 0, fila)
      cambiar(copia)
    },
    quitar: (i: number, nombre: string) => {
      const fila = filas[i]
      if (!fila) return
      setQuitado({ fila, indice: i, nombre })
      cambiar(filas.filter((_, j) => j !== i))
    },
    deshacer: () => {
      if (!quitado) return
      const copia = [...filas]
      copia.splice(Math.min(quitado.indice, copia.length), 0, quitado.fila)
      cambiar(copia)
      setQuitado(null)
    },
    olvidarQuitado: () => setQuitado(null),
  }
}

/** «Bloque quitado · Deshacer»: lo quitado vuelve a su sitio. */
const AvisoDeQuitado = ({ quitado, deshacer, cerrar }: { cerrar: () => void; deshacer: () => void; quitado: Quitado }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-caja border border-linea bg-papel-2 px-3 py-2 text-[13px]" role="status">
    <span className="min-w-0 truncate">{quitado.nombre} quitado. No se guarda hasta que pulses «Guardar».</span>
    <span className="flex items-center gap-1">
      <Boton icono={<Undo2 />} onClick={deshacer} pequeno>
        Deshacer
      </Boton>
      <Boton onClick={cerrar} pequeno tipo="discreto">
        Vale
      </Boton>
    </span>
  </div>
)

const Cabecera = ({ etiqueta, cuantos, descripcion, error }: { cuantos: number; descripcion?: string; error?: string; etiqueta: string }) => (
  <div>
    <p className="text-[13px] font-medium">
      {etiqueta} {cuantos > 0 && <span className="font-normal text-tenue">· {cuantos}</span>}
    </p>
    {descripcion && <p className="mt-0.5 text-xs text-tenue">{descripcion}</p>}
    {error && (
      <p className="mt-1 text-xs text-error" role="alert">
        {error}
      </p>
    )}
  </div>
)

export const Filas = ({ campo, ruta, soloLectura, tituloDe }: { campo: Campo; ruta: string; soloLectura: boolean; tituloDe?: (fila: Fila) => string }) => {
  const { filas, error, poner, mover, quitar, quitado, deshacer, olvidarQuitado } = useFilas(ruta)
  const { errores } = useFormulario()
  const [ultimaAnadida, setUltimaAnadida] = useState<null | string>(null)
  const campos = campo.campos ?? []
  const singular = campo.etiquetas?.singular ?? campo.etiqueta
  return (
    <div className="flex flex-col gap-2.5">
      <Cabecera cuantos={filas.length} descripcion={campo.descripcion} error={error} etiqueta={campo.etiqueta} />
      {filas.map((fila, i) => {
        const titulo = tituloDe?.(fila) || tituloDeFila(fila, campos) || `${singular} ${i + 1}`
        return (
          <Tarjeta
            clave={claveDe(fila, i)}
            conError={hayErroresEn(errores, `${ruta}.${i}`)}
            etiquetaDeQuitar={`Quitar «${titulo}»`}
            indice={i}
            key={claveDe(fila, i)}
            mover={mover}
            quitar={() => quitar(i, `«${titulo}»`)}
            soloLectura={soloLectura}
            titulo={titulo}
            total={filas.length}
            ultimaAnadida={ultimaAnadida}
          >
            {campos.map((hijo, j) => (
              <CampoGenerico campo={hijo} key={hijo.nombre ?? j} ruta={unir(`${ruta}.${i}`, hijo.nombre)} />
            ))}
          </Tarjeta>
        )
      })}
      {quitado && <AvisoDeQuitado cerrar={olvidarQuitado} deshacer={deshacer} quitado={quitado} />}
      {!soloLectura && (campo.max === undefined || filas.length < campo.max) && (
        <div>
          <Boton
            icono={<Plus />}
            onClick={() => {
              const nueva = idDeFila()
              poner([...filas, { ...valorInicial(campos), _clave: nueva }])
              setUltimaAnadida(nueva)
            }}
            pequeno
          >
            Añadir {singular.toLowerCase()}
          </Boton>
        </div>
      )}
    </div>
  )
}

export const Bloques = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { filas, error, poner, mover, quitar, quitado, deshacer, olvidarQuitado } = useFilas(ruta)
  const { errores } = useFormulario()
  const [menu, setMenu] = useState(false)
  const [ultimaAnadida, setUltimaAnadida] = useState<null | string>(null)
  const bloques = campo.bloques ?? []
  // Lo que el núcleo no ofrece a quien edita (el HTML a medida) se pinta si ya está, pero no se añade.
  const ofrecidos = bloques.filter((b) => !b.sinAnadir)
  const singular = (campo.etiquetas?.singular ?? 'Bloque').toLowerCase()
  // En una plantilla de un tipo o de una taxonomía, cada campo del bloque puede tomar un dato del documento (estructura/Vinculos.tsx).
  const plantilla = usePlantilla(ruta)
  return (
    <div className="flex flex-col gap-2.5">
      <Cabecera cuantos={filas.length} descripcion={campo.descripcion} error={error} etiqueta={campo.etiqueta} />
      {filas.map((fila, i) => {
        const definicion = bloques.find((b) => b.slug === fila.blockType)
        const nombre = definicion?.etiqueta ?? String(fila.blockType)
        const titulo = definicion ? tituloDeFila(fila, definicion.campos) : 'Bloque desconocido'
        return (
          <Tarjeta
            clave={claveDe(fila, i)}
            conError={hayErroresEn(errores, `${ruta}.${i}`)}
            etiquetaDeQuitar={`Quitar el bloque «${titulo || nombre}»`}
            indice={i}
            key={claveDe(fila, i)}
            mover={mover}
            quitar={() => quitar(i, `El bloque «${titulo || nombre}»`)}
            soloLectura={soloLectura}
            subtitulo={nombre}
            titulo={titulo}
            total={filas.length}
            ultimaAnadida={ultimaAnadida}
          >
            {definicion ? (
              plantilla && esBloqueDePlantilla(definicion.campos) ? (
                <BloqueDePlantilla bloque={definicion.slug} campos={definicion.campos} plantilla={plantilla} rutaDelBloque={`${ruta}.${i}`}>
                  {definicion.campos.map((hijo, j) => (
                    <CampoGenerico campo={hijo} key={hijo.nombre ?? j} ruta={unir(`${ruta}.${i}`, hijo.nombre)} />
                  ))}
                </BloqueDePlantilla>
              ) : (
                definicion.campos.map((hijo, j) => <CampoGenerico campo={hijo} key={hijo.nombre ?? j} ruta={unir(`${ruta}.${i}`, hijo.nombre)} />)
              )
            ) : (
              <p className="text-sm text-tenue">Este bloque ya no existe en el catálogo. Quítalo o pídeselo al equipo.</p>
            )}
          </Tarjeta>
        )
      })}
      {quitado && <AvisoDeQuitado cerrar={olvidarQuitado} deshacer={deshacer} quitado={quitado} />}
      {!soloLectura && ofrecidos.length > 0 && (campo.max === undefined || filas.length < campo.max) && (
        <div className="relative">
          <Boton icono={<Plus />} onClick={() => setMenu((v) => !v)} pequeno tipo={filas.length === 0 ? 'primario' : 'secundario'}>
            Añadir {singular}
          </Boton>
          {menu && (
            <>
              <button aria-label="Cerrar" className="fixed inset-0 z-20 cursor-default" onClick={() => setMenu(false)} type="button" />
              <ul className="absolute z-30 mt-1.5 grid w-72 gap-0.5 rounded-caja border border-linea bg-papel p-1.5 shadow-alta sm:w-[28rem] sm:grid-cols-2">
                {ofrecidos.map((b) => (
                  <li key={b.slug}>
                    <button
                      className="w-full rounded-boton px-2.5 py-2 text-left text-[13px] hover:bg-papel-2"
                      onClick={() => {
                        const nueva = idDeFila()
                        poner([...filas, { blockType: b.slug, ...valorInicial(b.campos), _clave: nueva }])
                        setUltimaAnadida(nueva)
                        setMenu(false)
                      }}
                      type="button"
                    >
                      {b.etiqueta}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}
